import { useState, FormEvent, useMemo, useCallback, useRef, useEffect, useLayoutEffect } from 'react';
import { isNil, omit } from 'ramda';
import clsx from 'clsx';

import { render } from 'src/tools/render.tools';
import { hasKey } from 'src/tools/object.tools';
import { ifTrue } from 'src/tools/logic.tools';

import { AnyValue, RecordObject, VoidFn } from 'src/models/general.model';
import { AutoCompleteDomains, VersionedPeriod } from 'src/models';
import { FormParserContext } from 'src/contexts/form.context';
import { FormScenario, FormShowAs, FormType } from 'src/models/form.model';
import { useDebouncedFn } from 'src/hooks';
import { CONFIG } from 'src/config';

import { Accordion } from 'src/components/Accordion';
import * as FormRenderers from 'src/components/FormRenderers';
import { Factory } from '../Factory';
import { ErrorsObject, GeneralError } from 'src/models/error.model';
import { FormError } from 'src/components/FormError';

import classes from './Form.module.scss';

type CallbackStorage = Record<string, (value: AnyValue) => void>;

interface Props<F extends RecordObject = RecordObject> {
  formData: F; // current form data
  errors: ErrorsObject; // form errors (validation or server-side errors)
  initialErrors?: ErrorsObject; // initial errors (visible before fields modifications)
  scenario: FormScenario; // form scenario
  setFormData: (form: F | ((form: F) => F)) => void; // form update callback
  actions?: RecordObject<VoidFn>; // add set of custom fns to be used
  initialState?: FormType;
  isDisabled?: boolean; // if true, form won't be editable, all fields will be disabled
  period?: VersionedPeriod;
}

const FORM_COMPONENTS: RecordObject<React.ReactNode> = {
  [FormShowAs.CHECKBOX_GROUP]: <FormRenderers.CheckboxGroup />,
  [FormShowAs.CHECKBOX]: <FormRenderers.Checkbox />,
  [FormShowAs.COMPANIES_AUTO_COMPLETE]: <FormRenderers.CompaniesAutoComplete />,
  [FormShowAs.CSI_AUTO_COMPLETE]: <FormRenderers.CSIAutoComplete />,
  [FormShowAs.DATE]: <FormRenderers.DateInput />,
  [FormShowAs.DROPDOWN]: <FormRenderers.SearchableDropdown />,
  [FormShowAs.FIN]: <FormRenderers.NumberInput isCurrency />,
  [FormShowAs.HEADING]: <FormRenderers.Heading />,
  [FormShowAs.LARGE_SELECT]: <FormRenderers.LargeSelect />,
  [FormShowAs.MAIN_GROUP]: <FormRenderers.GroupsAutoComplete domain={AutoCompleteDomains.MAIN_GROUP} />,
  [FormShowAs.NUMBER]: <FormRenderers.NumberInput />,
  [FormShowAs.INLINE_TEXTAREA_BUTTON]: <FormRenderers.InlineTextareaButton />,
  [FormShowAs.INLINE_NUMBER]: <FormRenderers.InlineNumberInput />,
  [FormShowAs.PERCENT]: <FormRenderers.NumberInput isPercent />,
  [FormShowAs.PHONE_NUMBER]: <FormRenderers.PhoneNumberInput />,
  [FormShowAs.PLACES]: <FormRenderers.PlacesInput />,
  [FormShowAs.PRIMARY_BUTTON]: <FormRenderers.PrimaryButton />,
  [FormShowAs.PROJ_COMPANY_TRADE_AUTO_COMPLETE]: <FormRenderers.TradesAutoComplete />,
  [FormShowAs.PROJ_SUBCONTRACTOR_AUTO_COMPLETE]: <FormRenderers.CompaniesAutoComplete isProject />,
  [FormShowAs.LEVELS_TREE_DROPDOWN]: <FormRenderers.LevelsTreeDropdown />,
  [FormShowAs.LEVEL_SEARCHABLE_DROPDOWN]: <FormRenderers.LevelSearchableDropdown />,
  [FormShowAs.RADIO]: <FormRenderers.RadioList />,
  [FormShowAs.SEARCH_DROPDOWN]: <FormRenderers.SearchableDropdown isSearchable />,
  [FormShowAs.SEARCH_NESTED_DROPDOWN]: <FormRenderers.SearchableNestedDropdown isSearchable />,
  [FormShowAs.NESTED_CONTINGENCY_DROPDOWN]: <FormRenderers.NestedContingencyDropdown />,
  [FormShowAs.SWITCH]: <FormRenderers.Switch />,
  [FormShowAs.TAGS_INPUT]: <FormRenderers.TagsInput />,
  [FormShowAs.TEXT_SWITCH]: <FormRenderers.TextSwitch />,
  [FormShowAs.TEXT]: <FormRenderers.PlainInput />,
  [FormShowAs.TRADE_GROUP]: <FormRenderers.GroupsAutoComplete />,
  [FormShowAs.UPLOAD_DOCUMENT]: <FormRenderers.UploadFile type='document' />,
  [FormShowAs.UPLOAD_PICTURE_BUTTON]: <FormRenderers.UploadPictureButton />,
  [FormShowAs.UPLOAD_PICTURE]: <FormRenderers.UploadFile type='picture' />,
  [FormShowAs.UPLOAD_PROFILE_PICTURE]: <FormRenderers.UploadProfilePicture />,
  [FormShowAs.UPLOAD_XLS_CSV_DOCUMENT]: <FormRenderers.UploadFile type='xlsCsvDocument' />,
  [FormShowAs.PLACEHOLDER_TEXT]: <FormRenderers.PlaceholderText />,
};

export default function Form<F extends RecordObject = RecordObject>({
  actions,
  formData,
  scenario,
  setFormData,
  errors,
  initialErrors,
  initialState,
  isDisabled: isDisabledForm,
  period,
}: Props<F>) {
  const modifiedFieldsRef = useRef<RecordObject<boolean>>({});

  const formRef = useRef<HTMLFormElement>(null);

  const [modifiedFields, setModifiedFields] = useState<RecordObject<boolean>>({});

  const delay = (scenario?._init?.validationDebounceDelay ?? CONFIG.validationDebounceDelay) + 50;

  const applyModifiedFields = useCallback(() => {
    setModifiedFields(modifiedFieldsRef.current);
  }, []);

  const debouncedApplyModifiedFields = useDebouncedFn(applyModifiedFields, delay);

  useEffect(() => {
    setModifiedFields({});
    modifiedFieldsRef.current = {};
  }, [initialState]);

  useLayoutEffect(() => {
    if (formRef.current?.parentElement) formRef.current.parentElement.scrollTop = 0;
  }, [initialState]);

  function handleOnSubmit(e: FormEvent) {
    e.preventDefault();
  }

  // process callbacks from renderers with values
  // cache all the callback to prevent continuous rerender
  const getHandleChangeFormData = useMemo(() => {
    const handlers: CallbackStorage = {};

    return (field: string) => {
      if (!handlers[field]) {
        handlers[field] = (value: AnyValue) => {
          setFormData(currentForm => {
            if (value === currentForm[field]) {
              return currentForm;
            }

            if (isNil(value)) {
              return omit([field], currentForm) as F;
            }

            return { ...currentForm, [field]: value };
          });

          modifiedFieldsRef.current = { ...modifiedFieldsRef.current, [field]: true };
          debouncedApplyModifiedFields();
        };
      }

      return handlers[field];
    };
  }, [debouncedApplyModifiedFields, setFormData]);

  function getWriteDataHandlers(fields?: string[]) {
    if (!fields?.length) {
      return [];
    }

    return fields.map(getHandleChangeFormData);
  }

  function getActionHandler(actionId?: string) {
    return actionId ? actions?.[actionId] : undefined;
  }

  function makeAdditionalReadDataObj(data: Array<string>) {
    const additionalReadDataObj: RecordObject<AnyValue> = {};

    for (const key of data) {
      additionalReadDataObj[key] = formData[key];
    }

    return additionalReadDataObj;
  }

  function renderItem(key: string) {
    const script = scenario[key];

    const isPlaceholderTextShown = script.placeholderText && script.readKey && !formData[script.readKey];

    if (!script.showAs || !hasKey(script.showAs!, FORM_COMPONENTS)) return;

    if (
      (script.isHiddenFromKey && !!formData[script.isHiddenFromKey]) ||
      (script.isVisibleFromKey && formData[script.isVisibleFromKey] === false)
    )
      return;

    let { isDisabled } = script;

    if (script.isDisabledFromKey) {
      isDisabled = !!formData[script.isDisabledFromKey];
    } else if (script.isEnabledFromKey) {
      isDisabled = !formData[script.isEnabledFromKey];
    } else if (script.isDisabled) {
      isDisabled = script.isDisabled;
    }

    let fieldError;

    if (modifiedFields[key] && !isDisabled) fieldError = errors[key];
    else if (!isDisabled) fieldError = initialErrors?.[key];

    return (
      <FormParserContext.Provider
        key={key}
        value={{
          value: formData[key],
          fieldName: key,
          script,
          config: scenario._init,
          onChange: getHandleChangeFormData(key),
          onSubmit: script.isSubmittable ? getActionHandler('submit') : undefined,
          readData: script.readKey ? formData[script.readKey] : undefined,
          additionalReadData: script.additionalReadKeys
            ? makeAdditionalReadDataObj(script.additionalReadKeys)
            : undefined,
          writeData: getWriteDataHandlers(script.writeKeys),
          action: getActionHandler(script.actionId),
          error: fieldError,
          isDisabled: isDisabled || isDisabledForm,
          actions,
          initialState,
          period,
        }}
      >
        <div className={clsx(classes.item, 'column f-1 j-end items-p8 o-hidden')} style={{ flex: script.flexInGroup }}>
          <FormRenderers.TopLabel />
          {!isPlaceholderTextShown && <FormRenderers.Label />}
          <Factory
            component={
              isPlaceholderTextShown ? FORM_COMPONENTS[FormShowAs.PLACEHOLDER_TEXT] : FORM_COMPONENTS[script.showAs!]
            }
          />
          <FormRenderers.BottomLabel />
        </div>
      </FormParserContext.Provider>
    );
  }

  const isRenderedGroup: Record<string, boolean> = {};

  const isRenderedSection: Record<string, boolean> = {};

  function renderGroup(group: string) {
    if (isRenderedGroup[group]) return;

    isRenderedGroup[group] = true;

    const fields = Object.keys(scenario).filter(k => scenario[k].group === group);

    const renderedFields = render(fields, renderItem).filter(Boolean);

    return ifTrue(renderedFields.length, renderedFields);
  }

  function renderItemOrGroup(key: string) {
    const script = scenario[key];

    const content = script.group ? renderGroup(script.group) : renderItem(key);

    return (
      content && (
        <li key={key} className={clsx(classes.element, 'row items-1p6 a-start w-100p')}>
          {content}
        </li>
      )
    );
  }

  const closedSections = (formData._closedSections as string[]) ?? [];

  function renderSection(section: string) {
    if (isRenderedSection[section]) {
      return null;
    }

    isRenderedGroup[section] = true;

    const fields = Object.keys(scenario).filter(k => scenario[k].section === section);

    return (
      <Accordion key={section} label={section} isOpenDefault={!closedSections.includes(section)} isSmall>
        <ul className={clsx(classes.section, 'column f-1')}>{render(fields, renderItemOrGroup)}</ul>
      </Accordion>
    );
  }

  function renderElement(key: string) {
    const script = scenario[key];

    if (script.section) {
      return renderSection(script.section);
    }

    return renderItemOrGroup(key);
  }

  function renderGeneralError(item: GeneralError) {
    return <FormError key={item.id} {...item} />;
  }

  function renderGeneralErrors() {
    return (
      <div className={clsx('w-100p column items-1p6', classes.errors)}>
        {render(errors._general, renderGeneralError)}
      </div>
    );
  }

  return (
    <form className='w-100p' onSubmit={handleOnSubmit} ref={formRef}>
      {ifTrue(errors._general?.length, renderGeneralErrors)}
      <ul>{render(Object.keys(scenario), renderElement)}</ul>
    </form>
  );
}
